/**
 * Geld in Raporto ist immer eine Ganzzahl in Rappen.
 *
 * Diese Regel ist nicht verhandelbar und der Grund ist einfach: 0.1 + 0.2 ergibt
 * in Fliesskomma nicht 0.3. Eine Rechnung mit dreissig Positionen sammelt diese
 * Fehler, und am Ende weicht die Summe im PDF von der Summe in der Datenbank ab.
 * Nachträglich umzustellen hiesse, jede bestehende Rechnung neu zu rechnen.
 *
 * Ein Franken = 100 Rappen. CHF 1'234.55 ist die Zahl 123455.
 */

export type Rappen = number

export function toRappen(chf: number | string): Rappen {
  const n = typeof chf === 'string' ? Number(chf.replace(/'/g, '').replace(/,/g, '.')) : chf
  if (!Number.isFinite(n)) throw new Error(`Kein gültiger Betrag: ${chf}`)
  // Multiplikation vor dem Runden, damit 19.99 nicht zu 1998 wird
  return Math.round(n * 100)
}

export function toChf(rp: Rappen): number {
  return rp / 100
}

/** Kaufmännisch gerundet auf 5 Rappen – für Barzahlungen und Endsummen. */
export function roundTo5Rp(rp: Rappen): Rappen {
  return Math.round(rp / 5) * 5
}

/**
 * Prozentanteil eines Betrags, kaufmännisch auf den Rappen gerundet.
 * Wird für MWST und Rabatte verwendet und muss auf Server und Client
 * dasselbe Ergebnis liefern – deshalb liegt sie hier und nicht zweimal.
 */
export function percentOf(rp: Rappen, percent: number): Rappen {
  return Math.round((rp * percent) / 100)
}

/** Menge × Einzelpreis. Menge darf drei Nachkommastellen haben (z.B. 12.375 m²). */
export function lineTotal(quantity: number, unitPriceRp: Rappen): Rappen {
  return Math.round(quantity * unitPriceRp)
}

const CHF_FORMAT = new Intl.NumberFormat('de-CH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

/** "1'234.55" – Schweizer Schreibweise mit Hochkomma als Tausendertrennzeichen. */
export function formatChf(rp: Rappen): string {
  return CHF_FORMAT.format(toChf(rp))
}

export function formatChfWithUnit(rp: Rappen): string {
  return `CHF ${formatChf(rp)}`
}
